import React from 'react';
import { useRoutedNavigation } from "presentation/hooks/useRoutedNavigation";
import styled from 'styled-components';

interface LinkButtonCardProps {
  title: string;
  href: string;
  icon: React.ReactNode;
  external?: boolean;
  disabled?: boolean;
}

const LinkCardWrapper = styled.div<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-decoration: none;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  position: relative;
  opacity: ${props => props.$disabled ? 0.5 : 1};
  pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
`;

const IconCircle = styled.div<{ $disabled?: boolean }>`
  width: 128px;
  height: 128px;
  border-radius: 64px;
  border: 1px solid ${props => props.$disabled ? '#999' : 'var(--portal-primary)'};
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.3s ease;

  svg {
    font-size: 3.5rem;
    color: ${props => props.$disabled ? '#999' : 'var(--portal-primary)'};
    transition: all 0.3s ease;
  }

  &:hover {
    background: ${props => props.$disabled ? 'transparent' : 'var(--portal-primary)'};

    svg {
      color: ${props => props.$disabled ? '#999' : '#fff'};
    }
  }
`;

const LinkTitle = styled.span<{ $disabled?: boolean }>`
  font-weight: 500;
  color: ${props => props.$disabled ? '#999' : '#333'};
  text-align: center;
  font-size: 0.875rem;
  line-height: 1.4;
  transition: color 0.2s ease;

  ${LinkCardWrapper}:hover & {
    color: ${props => props.$disabled ? '#999' : 'var(--portal-secondary)'};
    font-weight: ${props => props.$disabled ? 500 : 600};
  }
`;

export default function LinkButtonCard({ title, href, icon, external, disabled }: LinkButtonCardProps) {
  const { navigateTo: navigate } = useRoutedNavigation();

  const handleClick = () => {
    if (disabled) return;

    if (external) {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else {
      navigate(href);
    }
  };

  return (
    <LinkCardWrapper onClick={handleClick} $disabled={disabled}>
      <IconCircle $disabled={disabled}>
        {icon}
      </IconCircle>

      <LinkTitle $disabled={disabled}>
        {title}
      </LinkTitle>
    </LinkCardWrapper>
  );
}
